// modules
import { get, isEmpty } from 'lodash'
import { createSelector } from 'reselect'

// utils
import addAllValue from 'src/utils/functions/addAllValue'

const auth = (state = {}) => get(state, ['auth'], {})

export const isUserAuthorized$ = createSelector(auth, (auth = {}) =>
  get(auth, ['isUserAuthorized'], false)
)

export const isManuallyLoggedOut$ = createSelector(auth, (auth = {}) =>
  get(auth, ['isManuallyLoggedOut'], false)
)

export const userProfile$ = createSelector(auth, (auth = {}) =>
  get(auth, ['profile'], {})
)

export const userUuid$ = createSelector(userProfile$, (profile = {}) =>
  get(profile, ['uuid'], null)
)

export const userName$ = createSelector(userProfile$, (profile = {}) =>
  get(profile, ['fullName'], '')
)

export const isTaskSuperManager$ = createSelector(
  userProfile$,
  (profile = {}) => get(profile, ['isTaskSuperManager'], false)
)

export const userTimeZone$ = createSelector(userProfile$, (profile = {}) =>
  get(profile, ['companyTimezone'], '')
)

export const userRole$ = createSelector(userProfile$, (profile = {}) => {
  const roles = get(profile, ['roles'], {})
  const userRole = get(Object.keys(roles), [0], '')

  return userRole
})

export const userEproms$ = createSelector(userProfile$, (profile = {}) =>
  get(profile, ['clinicalPathwayUuids'], [])
)

export const eventsEnable$ = createSelector(
  userProfile$,
  (profile) => !!profile?.isShowPatientEventsReport
)

export const rpmOutOfRangeReportEnable$ = createSelector(
  userProfile$,
  (profile) => !!profile?.isShowRpmOutOfRangeReport
)

export const careGapReportEnable$ = createSelector(
  userProfile$,
  (profile) => !!profile?.isShowCareGapReport
)

export const patientMeasurementReportEnable$ = createSelector(
  userProfile$,
  (profile) => !!profile?.isShowPatientMeasurementReport
)

export const carePlanEnable$ = createSelector(
  userProfile$,
  (profile = {}) =>
    !!(profile?.licenseModule ?? []).find(
      ({ name }: { name: string }) => name === 'Careplan'
    )
)

export const measurementEnable$ = createSelector(
  userProfile$,
  (profile = {}) =>
    !!(profile?.licenseModule ?? []).find(
      ({ name }: { name: string }) => name === 'Measurement'
    )
)

// TODO refactor all of this mess
export const payerLicenseEnable$ = createSelector(
  userProfile$,
  (profile = {}) =>
    !!(profile?.licenseModule ?? []).find(
      ({ name }: { name: string }) => name === 'Payer'
    )
)

export const referralLicense$ = createSelector(
  userProfile$,
  (profile = {}) =>
    !!(profile?.licenseModule ?? []).find(
      ({ name }: { name: string }) => name === 'Referral'
    )
)

export const companyLanguages$ = createSelector(userProfile$, (profile = {}) =>
  get(profile, ['companyLanguages'], [])
)

export const companyLanguagesWithAll$ = createSelector(
  companyLanguages$,
  (companyLanguages = []) =>
    addAllValue(companyLanguages, true, 'All languages')
)

export const companyUuid$ = createSelector(userProfile$, (profile = {}) =>
  get(profile, ['companyUuid'], null)
)

export const companyTitle$ = createSelector(userProfile$, (profile = {}) =>
  get(profile, ['companyTitle'], '')
)

export const accessTo$ = createSelector(userProfile$, (profile = {}) =>
  get(profile, ['accessTo'], [])
)

// TODO isProviderUser, etc ?
export const accessType$ = createSelector(userProfile$, (profile = {}) =>
  get(profile, ['accessType'], null)
)

export const gridSettings$ = createSelector(
  userProfile$,
  ({ gridSettings = {} }) => gridSettings || null
)

export const defaultGridSettings$ = createSelector(
  userProfile$,
  ({ defaultGridSettings = {} }) => defaultGridSettings || null
)

export const gridSettingsByName$ = (name = '') =>
  createSelector(
    gridSettings$,
    defaultGridSettings$,
    (gridSettings = {}, defaultGridSettings = {}) => {
      const savedSettings = gridSettings?.[name] || null
      const defaultSettings = defaultGridSettings?.[name] || null

      return !isEmpty(savedSettings) ? savedSettings : defaultSettings
    }
  )

export const patientDemographicsPermission$ = createSelector(
  userProfile$,
  (profile = {}) => get(profile, ['patientDemographicsPermission'], null)
)

export const isAvailableStarMeasurements$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isAvailableStarMeasurements || false
)

export const isAvailablePatientAppointments$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isAvailablePatientAppointments || false
)

export const accessToInterventionDetails$ = createSelector(
  accessTo$,
  (accessTo = []) => accessTo.includes('Intervention Details')
)

export const isTaskDueDateRequired$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isTaskDueDateRequired || false
)

export const isForceCallBackgroundImage$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.isForceCallBackgroundImage || false
)

export const defaultAgencyCaseManager$ = createSelector(
  userProfile$,
  (profile = {}) => profile?.defaultAgencyCaseManager || null
)
